import { useEffect, useMemo, useState } from "react";
import { Spinner } from "../../atoms/Spinner";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import useAuth from "../../../hooks/useAuth";
import { toast } from "react-toastify";
import Plans, {
  decideGeneralName,
  PartnerPaidBy,
  PlanNames,
} from "../../Plans";
import {
  isFreePlan,
  PLAN_MAPPINGS,
  PlanTier,
  usePermissions,
} from "../../../utils/permissions";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  HiExclamation,
  HiExclamationCircle,
  HiStar,
  HiOutlineTrash,
  HiCreditCard,
} from "react-icons/hi";
import moment from "moment";
import { currencySymbolMapper, CurrencyType } from "../../../utils/currency";
import { FaSms } from "react-icons/fa";
import { FaWhatsapp } from "react-icons/fa6";
import { useGetEntityOwnerData } from "../../../hooks/entity";
import { AddCreditModal } from "./entities/AddCreditModal";
import ConfirmModal from "../../public/ConfirmModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import ActiveTrialBanner from "../../molecules/ActiveTrialBanner";
import { CustomButton } from "../../atoms/CustomButton";
import { ModalShell } from "../../atoms/ModalShell";
import { DataTable } from "../../atoms/DataTable";
import { StatusBadge, BadgeTone } from "../../atoms/StatusBadge";
import { SideDrawer } from "../../atoms/SideDrawer";
import { EmptyState } from "../../atoms/EmptyState";
import {
  useCancelPlanSubscription,
  useCardUpdateLink,
  useGetOwnerPlans,
  useRemovePaymentMethod,
  useRequestInvoiceHistoryDetails,
  useSubscriptionCardDetail,
  useSubscriptionDetail,
  useUpdateSubscriptionCard,
} from "../../../hooks/billing";
import { useGetTrialStatus } from "../../../hooks/promoCode";
import PromoCodeModal from "../../modals/PromoCodeModal";
import {
  forgetPlanChanges,
  usePlanChanges,
  useResumeSubscription,
} from "../../../hooks/billing/planChange";
import { formatPayModDate } from "../../../services/planChange";
import {
  findSelfPaidPlan,
  isSelfPaidPlan,
  PlanStatusEntry,
} from "../../planChange/planCatalog";
import PlanChangeBanners from "../../planChange/PlanChangeBanners";
import { usePlanChangeFormat } from "../../planChange/usePlanChangeFormat";

type Card = {
  paymentMethodId: string;
  brand: string;
  country: string;
  expDate: string;
  expMonth?: number;
  expYear?: number;
  last4: string;
};

const CARD_EXPIRY_WARNING_DAYS = 60;

const cardExpiryState = (
  expMonth?: number,
  expYear?: number
): "expired" | "soon" | null => {
  if (!expMonth || !expYear) return null;
  const lastValidDay = moment({
    year: expYear,
    month: expMonth - 1,
    day: 1,
  }).endOf("month");
  if (!lastValidDay.isValid()) return null;
  if (lastValidDay.isBefore(moment())) return "expired";
  return lastValidDay.diff(moment(), "days") <= CARD_EXPIRY_WARNING_DAYS
    ? "soon"
    : null;
};

const formatPaymentAmount = (priceInCents?: number, currency?: string) => {
  if (priceInCents == null) return "-";
  const cur = (currency || "USD") as CurrencyType;
  const symbol = currencySymbolMapper[cur] ?? "";
  const value = (priceInCents / 100).toLocaleString();
  return cur === "CHF" ? `${symbol} ${value}` : `${symbol}${value}`;
};

const Billing = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const getPlanTierFromName = (planName: string): PlanTier | null => {
    for (const [tier, plans] of Object.entries(PLAN_MAPPINGS)) {
      if ((plans as readonly string[]).includes(planName)) {
        return tier as PlanTier;
      }
    }
    return null;
  };

  const getPlanTierDisplayName = (planName: string): string => {
    const tier = getPlanTierFromName(planName);
    if (!tier) return "";

    const tierKeys: Record<PlanTier, string> = {
      [PlanTier.FREE]: "plans.free",
      [PlanTier.PREMIUM]: "plans.premium",
      [PlanTier.LARGE_BUSINESS]: "plans.largeBusiness",
      [PlanTier.ENTERPRISE]: "plans.enterprise",
      [PlanTier.BUSINESS_PARTNER]: "plans.businessPartner",
      [PlanTier.PROMO_ENTERPRISE_TRIAL]: "plans.promoEnterpriseTrial",
    };

    return t(tierKeys[tier]);
  };

  const { customer_identification, user, upgradeNow, onOffUpgradeNow } =
    useAuth();

  const { getPlanTier, hasFeatureAccess } = usePermissions(user);

  const canSendSms = hasFeatureAccess("SEND_SMS");
  const canSendWhatsapp = hasFeatureAccess("SEND_WHATSAPP");
  const { data: smsCreditData } = useGetEntityOwnerData(
    customer_identification,
    user.entity_identification,
    "sms_credit",
    { enabled: canSendSms }
  );
  const { data: whatsappCreditData } = useGetEntityOwnerData(
    customer_identification,
    user.entity_identification,
    "whatsapp_credit",
    { enabled: canSendWhatsapp }
  );
  const smsCredit = smsCreditData?.entity_other_data?.sms_credit ?? 0;
  const whatsappCredit =
    whatsappCreditData?.entity_other_data?.whatsapp_credit ?? 0;
  // Credit is only spendable once a WhatsApp number is in service, which support
  // sets up: no number, no recharge.
  const { data: whatsappSenderData } = useGetEntityOwnerData(
    customer_identification,
    user.entity_identification,
    "whatsapp_sender",
    { enabled: canSendWhatsapp }
  );
  const hasWhatsappSender = Boolean(
    whatsappSenderData?.entity_other_data?.whatsapp_sender?.from
  );
  const [showSmsCreditModal, setShowSmsCreditModal] = useState(false);
  const [showWhatsappCreditModal, setShowWhatsappCreditModal] = useState(false);
  const currentTier = getPlanTier();
  const upgradeTargetPlan =
    currentTier === PlanTier.PREMIUM
      ? PlanTier.ENTERPRISE
      : currentTier === PlanTier.ENTERPRISE ||
        currentTier === PlanTier.PROMO_ENTERPRISE_TRIAL
      ? PlanTier.LARGE_BUSINESS
      : currentTier === PlanTier.LARGE_BUSINESS
      ? undefined
      : PlanTier.PREMIUM;

  const [selectedPlan, setSelectedPlan] = useState("My plans");
  const [selectedPlanLabel, setSelectedPlanLabel] =
    useState<string>("My plans");
  const [selectedPlanStatus, setSelectedPlanStatus] = useState<
    undefined | "ACTIVE" | "SUCCEEDED" | "CANCELED" | "ENDED"
  >(undefined);
  const [selectedActivePlanDefaultCardId, setSelectedActivePlanDefaultCardId] =
    useState<string>();
  const [planCards, setPlanCards] = useState<Card[] | []>([]);
  const [planHistoryData, setPlanHistoryData] = useState<any[] | []>([]);
  const [fetchPlanHistoryLoading, setFetchPlanHistoryLoading] = useState(false);
  const [planHistoryError, setPlanHistoryError] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [fetchPlanCardsLoading, setFetchPlanCardsLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showConfirmDeleteCard, setShowConfirmDeleteCard] = useState(false);
  const [selectedCardPaymentMethodId, setSelectedCardPaymentMethodId] =
    useState<string>("");
  const [showPromoCodeModal, setShowPromoCodeModal] = useState(false);
  const [showPlanDrawer, setShowPlanDrawer] = useState(false);
  const [selectedPlanPaidBy, setSelectedPlanPaidBy] =
    useState<PartnerPaidBy | null>(null);

  useEffect(() => {
    if (upgradeNow) {
      setShowPlansModal(true);
      onOffUpgradeNow(false);
    }
  }, [upgradeNow, onOffUpgradeNow]);

  useEffect(() => {
    const handleOpenPlansModal = () => {
      setShowPlansModal(true);
    };

    window.addEventListener("openPlansModal", handleOpenPlansModal);

    return () => {
      window.removeEventListener("openPlansModal", handleOpenPlansModal);
    };
  }, []);

  const {
    isLoading,
    isError,
    data: res,
    refetch,
  } = useGetOwnerPlans(customer_identification, user.entity_identification);

  const selfPaid = useMemo(() => findSelfPaidPlan(res?.planList), [res]);
  const offersPlanChange =
    Boolean(selfPaid) && !res?.planList?.some((item: any) => item.paid_by);
  const selectedEntry: PlanStatusEntry | undefined = res?.planList?.find(
    (item: PlanStatusEntry) => item.plan === selectedPlan
  );
  const selectedSelfPaid =
    selectedEntry && isSelfPaidPlan(selectedEntry) ? selectedEntry : null;
  const { data: planChanges } = usePlanChanges(
    customer_identification,
    user.entity_identification,
    Boolean(selfPaid)
  );
  const { mutate: resumeSubscription, isLoading: isResuming } =
    useResumeSubscription();
  const { k: pc, date: pcDate, planName: pcPlanName } = usePlanChangeFormat();

  const selectedPeriodEnd = (() => {
    if (selectedSelfPaid?.current_period_end) {
      return selectedSelfPaid.current_period_end;
    }
    const dates = planHistoryData
      .map((item: any) => moment(item.paymentNextDate))
      .filter((date) => date.isValid());
    return dates.length > 0 ? moment.max(dates).toISOString() : null;
  })();

  const { data: trialStatus } = useGetTrialStatus(
    customer_identification,
    user.entity_identification
  );

  const { mutate: cancelPlan, isLoading: isCanceling } =
    useCancelPlanSubscription();

  const {
    mutate: updateCard,
    variables,
    isLoading: isUpdating,
  } = useUpdateSubscriptionCard();

  const {
    mutate: deleteCard,
    variables: removeVariable,
    isLoading: isDeleting,
  } = useRemovePaymentMethod();

  const { mutate: getInvoiceHistoryDetails, isLoading: isGettingInvoice } =
    useRequestInvoiceHistoryDetails();

  const { mutate: getSubscriptionCardDetail } = useSubscriptionCardDetail();

  const { mutate: getSubscriptionDetail } = useSubscriptionDetail();

  const isOneOffPurchasePlan = (plan: string) =>
    plan === "SUMOC_SMSGTO20USD" ||
    plan === "SUMOC_WAGTO20USD" ||
    plan === "SUMOC_PAYREQ01USD" ||
    plan?.startsWith("SUMOC_ROI");

  const handleFetchPlanCards = (
    plan: string,
    status: "ACTIVE" | "CANCELED" | "SUCCEEDED" | "ENDED" | undefined,
    description: string
  ) => {
    setPlanCards([]);
    setSelectedPlan(plan);
    setSelectedPlanLabel(description);
    setSelectedPlanStatus(status);
    setFetchPlanCardsLoading(true);

    if (
      !isFreePlan(plan) &&
      status !== "CANCELED" &&
      status !== "ENDED" &&
      !isOneOffPurchasePlan(plan)
    ) {
      getSubscriptionCardDetail(
        {
          customer_identification: customer_identification,
          entityIdentification: user.entity_identification,
          plan: plan,
        },
        {
          onSuccess: (value) => {
            setPlanCards(value.planCards);
            if (value.selectedActivePlanDefaultCardId !== "") {
              setSelectedActivePlanDefaultCardId(
                value.selectedActivePlanDefaultCardId
              );
            }
          },
          onSettled: () => setFetchPlanCardsLoading(false),
        }
      );
    } else {
      setFetchPlanCardsLoading(false);
      toast.info(t("dashboard.billings.toast-04"));
    }
  };

  const handleFetchPlanHistory = (plan: string) => {
    setPlanHistoryData([]);
    setPlanHistoryError(false);
    setFetchPlanHistoryLoading(true);
    getSubscriptionDetail(
      {
        customer_identification: customer_identification,
        entityIdentification: user.entity_identification,
        selectedPlan: plan,
      },
      {
        onSuccess: (value: any) => {
          setPlanHistoryData(value.data.element);
        },
        onError: () => setPlanHistoryError(true),
        onSettled: () => setFetchPlanHistoryLoading(false),
      }
    );
  };

  const handleOpenPlanDrawer = (
    plan: string,
    status: "ACTIVE" | "CANCELED" | "SUCCEEDED" | "ENDED" | undefined,
    description: string,
    paidBy: PartnerPaidBy | null = null
  ) => {
    setSelectedPlanPaidBy(paidBy);
    handleFetchPlanCards(plan, status, description);
    handleFetchPlanHistory(plan);
    setShowPlanDrawer(true);
  };

  const handleCancelSelectedPlanSubscription = () => {
    cancelPlan(
      {
        customer_identification: customer_identification,
        entityIdentification: user.entity_identification,
        selectedPlan: selectedPlan,
        atPeriodEnd: Boolean(selectedSelfPaid),
        successMessage: selectedSelfPaid
          ? pcDate(selectedPeriodEnd)
            ? pc("cancelSubscription.toast", {
                date: pcDate(selectedPeriodEnd),
              })
            : pc("cancelSubscription.toastNoDate")
          : undefined,
      },
      {
        onSuccess: () => {
          forgetPlanChanges();
          setShowCancelModal(false);
          setShowPlanDrawer(false);
          setPlanCards([]);
          setPlanHistoryData([]);
          setSelectedPlan("My plans");
          setSelectedPlanStatus(undefined);
          setSelectedPlanLabel("My plans");
        },
        onError: () => {
          setShowCancelModal(false);
        },
      }
    );
  };

  const handleMakeAsDefault = (paymentMethodId: string) => {
    updateCard(
      {
        customer_identification: customer_identification,
        entityIdentification: user.entity_identification,
        selectedPlan: selectedPlan,
        payment_method_id: paymentMethodId,
      },
      {
        onSuccess: () =>
          handleFetchPlanCards(
            selectedPlan,
            selectedPlanStatus,
            selectedPlanLabel
          ),
      }
    );
  };

  const handleRemoveCard = () => {
    if (selectedCardPaymentMethodId) {
      deleteCard(
        {
          customer_identification: customer_identification,
          entityIdentification: user.entity_identification,
          selectedPlan: selectedPlan,
          payment_method_id: selectedCardPaymentMethodId,
        },
        {
          onSuccess: () => {
            setShowConfirmDeleteCard(false);
            setSelectedCardPaymentMethodId("");

            handleFetchPlanCards(
              selectedPlan,
              selectedPlanStatus,
              selectedPlanLabel
            );
          },
        }
      );
    }
  };

  const handleGetInvoice = (id: string) => {
    getInvoiceHistoryDetails(
      {
        invoiceId: id,
      },
      {
        onSuccess: (res: any) => {
          const link = document.createElement("a");
          link.href = res.data.element.invoicePDFLink;
          document.body.appendChild(link);
          link.click();
          link.setAttribute("disabled", "true");
          document.body.removeChild(link);
        },
      }
    );
  };

  useEffect(() => {
    if (
      searchParams &&
      searchParams.has("plan-name") &&
      searchParams.has("status") &&
      searchParams.get("status") === "true"
    ) {
      navigate(
        `/control-panel?plan=${decideGeneralName(
          searchParams.get("plan-name") as PlanNames
        )}&plan_name=${searchParams.get("plan-name")}&status=true`,
        {
          replace: true,
        }
      );
    }
  }, [navigate, searchParams]);

  const columns: ColumnDef<Card>[] = [
    {
      header: () => <span>{t("dashboard.billings.column-05")}</span>,
      accessorKey: "last4",
      cell: (props) => (
        <span className="whitespace-nowrap font-medium tabular-nums text-gray-900">
          **** **** **** {props.row.original.last4}
        </span>
      ),
    },
    {
      header: () => <span>{t("dashboard.billings.column-01")}</span>,
      accessorKey: "brand",
      cell: (props) => (
        <span className="capitalize">{props.row.original.brand}</span>
      ),
    },
    {
      header: () => <span>{t("dashboard.billings.column-02")}</span>,
      accessorKey: "country",
    },
    {
      header: () => <span>{t("dashboard.billings.column-03")}</span>,
      accessorKey: "expDate",
      cell: (props) => {
        const expiryState = cardExpiryState(
          props.row.original.expMonth,
          props.row.original.expYear
        );
        return (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <span className="tabular-nums">{props.row.original.expDate}</span>
            {expiryState && (
              <StatusBadge
                color={expiryState === "expired" ? "failure" : "warning"}
                icon={HiExclamationCircle}
                shape="rounded"
              >
                {expiryState === "expired"
                  ? t("dashboard.billings.cardExpired")
                  : t("dashboard.billings.cardExpiresSoon")}
              </StatusBadge>
            )}
          </span>
        );
      },
    },
    {
      id: "Actions",
      header: () => <span className="sr-only">{t("common.actions")}</span>,
      cell: (props) => {
        const isDefaultCard =
          selectedActivePlanDefaultCardId ===
          props.row.original.paymentMethodId;
        const isSettingDefault =
          variables === props.row.original.paymentMethodId && isUpdating;
        const isRemoving =
          removeVariable === props.row.original.paymentMethodId && isDeleting;

        return (
          <div className="flex items-center justify-end gap-1">
            {isDefaultCard ? (
              <span
                className="flex p-1.5 text-blue-600"
                title={t("dashboard.billings.badge") as string}
                translate="no"
              >
                <HiStar size={20} />
              </span>
            ) : (
              <CustomButton
                variant="none"
                size="icon"
                className="text-gray-400 enabled:hover:text-blue-600"
                onClick={() =>
                  handleMakeAsDefault(props.row.original.paymentMethodId)
                }
                disabled={isSettingDefault}
                isLoading={isSettingDefault}
                tooltip={t("dashboard.billings.btn-03") as string}
                icon={<HiStar size={20} />}
              />
            )}
            <CustomButton
              variant="none"
              size="icon"
              className="text-gray-400 enabled:hover:text-red-600"
              onClick={() => {
                setSelectedCardPaymentMethodId(
                  props.row.original.paymentMethodId
                );
                setShowConfirmDeleteCard(true);
              }}
              disabled={isRemoving}
              isLoading={isRemoving}
              tooltip={t("common.delete") as string}
              icon={<HiOutlineTrash size={18} />}
            />
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: planCards,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { mutate: requestCardUpdateLink, isLoading: isOpeningCardUpdate } =
    useCardUpdateLink();

  const openCardUpdate = () =>
    requestCardUpdateLink(
      {
        customer_identification,
        entityIdentification: user.entity_identification,
        plan: selectedPlan,
      },
      { onSuccess: (link) => window.location.assign(link.url) }
    );

  if (isLoading)
    return (
      <span translate="no">
        <Spinner size="sm" />
      </span>
    );

  if (isError)
    return (
      <CustomButton
        onClick={() => refetch()}
        title={t("dashboard.billings.btn-05") as string}
        variant="primary"
      />
    );

  const getPaymentStatusLabel = (status?: string): string => {
    if (!status) return "";
    const labels: Record<string, string> = {
      ACTIVE: t("paymentHistory.status.active"),
      SUCCEEDED: t("paymentHistory.status.succeeded"),
      CANCELED: t("paymentHistory.status.canceled"),
      ENDED: t("paymentHistory.status.ended"),
      UNPAID: t("paymentHistory.status.unpaid"),
      REFUNDED: t("paymentHistory.status.refunded"),
      ERROR: t("paymentHistory.status.error"),
      PROCESSING: t("paymentHistory.status.processing"),
    };
    if (labels[status]) return labels[status];
    const words = status.replace(/_/g, " ").toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
  };

  const STATUS_TONES: Record<string, BadgeTone> = {
    SUCCEEDED: "success",
    ACTIVE: "info",
    UNPAID: "failure",
    ERROR: "failure",
    REFUNDED: "purple",
    CANCELED: "gray",
    ENDED: "gray",
  };

  const statusTone = (status?: string): BadgeTone =>
    (status && STATUS_TONES[status]) || "gray";

  const renderPlanStatus = (
    status: "ACTIVE" | "SUCCEEDED" | "CANCELED" | "ENDED"
  ) => {
    return (
      <StatusBadge color={statusTone(status)} shape="full">
        {getPaymentStatusLabel(status)}
      </StatusBadge>
    );
  };

  const renderPaymentStatus = (status?: string) => (
    <StatusBadge color={statusTone(status)} shape="full">
      {getPaymentStatusLabel(status)}
    </StatusBadge>
  );

  const getPlanLabel = (plan: string, description: string) => {
    switch (true) {
      case plan === "SUMOC_SMSGTO20USD":
        return t("dashboard.entities.viewEntityUser.addCreditModal.smsCredit");
      case plan === "SUMOC_WAGTO20USD":
        return t(
          "dashboard.entities.viewEntityUser.addCreditModal.whatsappCredit"
        );
      case plan === "SUMOC_PAYREQ01USD":
        return t("dashboard.billings.paymentRequest");
      case plan?.startsWith("SUMOC_ROI"):
        return t("dashboard.roi_guarantee.guarantee_pack");
      default:
        return description;
    }
  };

  const endsWithoutRenewal = (paidBy: PartnerPaidBy | null) =>
    paidBy?.auto_renew === false &&
    Boolean(paidBy.ends_at) &&
    moment(paidBy.ends_at).isValid();

  const renderPartnerEmail = (email: string | null) =>
    email && (
      <p>
        {t("dashboard.billings.partnerContact")}:{" "}
        <a
          href={`mailto:${email}`}
          className="break-all font-medium text-blue-700 hover:underline"
        >
          {email}
        </a>
      </p>
    );

  const renderPartnerEndNotice = (paidBy: PartnerPaidBy, compact = false) => (
    <div
      className={`rounded-lg border border-amber-200 bg-amber-50 ${
        compact ? "p-2.5" : "p-3"
      }`}
    >
      <div className="flex items-start gap-2">
        <HiExclamation
          className="mt-0.5 shrink-0 text-amber-600"
          size={compact ? 16 : 20}
        />
        <div
          className={`flex min-w-0 flex-col gap-2 text-amber-800 ${
            compact ? "text-xs" : "text-sm"
          }`}
        >
          <p>
            {paidBy.display_name
              ? t("dashboard.billings.providedUntilNotice", {
                  name: paidBy.display_name,
                  date: moment(paidBy.ends_at).format("LL"),
                })
              : t("dashboard.billings.providedUntilPartnerNotice", {
                  date: moment(paidBy.ends_at).format("LL"),
                })}
          </p>
          {renderPartnerEmail(paidBy.contact_email)}
        </div>
      </div>
    </div>
  );

  const PlanCard = ({
    plan,
    status,
    description,
    paidBy,
    isSelected,
    onClick,
  }: any) => (
    <div
      className={`flex h-full flex-col gap-3 rounded-lg border p-4 transition-colors ${
        isSelected
          ? "border-blue-500 bg-blue-50"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900">
          {getPlanLabel(plan, description)}
        </h3>
        {renderPlanStatus(status)}
      </div>

      <p className="truncate text-xs text-gray-500" translate="no">
        <span className="font-medium text-gray-700">
          {t("dashboard.billings.plan-id")}:
        </span>{" "}
        {plan}
      </p>

      {getPlanTierDisplayName(plan) && (
        <div className="flex flex-wrap gap-2">
          <StatusBadge color="gray" shape="full">
            {getPlanTierDisplayName(plan)}
          </StatusBadge>
        </div>
      )}

      {paidBy !== null &&
        (endsWithoutRenewal(paidBy) ? (
          renderPartnerEndNotice(paidBy, true)
        ) : (
          <div className="flex flex-col gap-1 text-xs text-gray-600">
            <p>
              {paidBy.display_name
                ? t("dashboard.billings.providedBy", {
                    name: paidBy.display_name,
                  })
                : t("dashboard.billings.providedByPartner")}
            </p>
            {renderPartnerEmail(paidBy.contact_email)}
          </div>
        ))}

      <div className="mt-auto pt-1">
        <CustomButton
          variant="secondary"
          size="sm"
          className="w-full"
          icon="arrowRight"
          title={t("common.viewDetails") as string}
          onClick={onClick}
        />
      </div>
    </div>
  );

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6 px-4 py-6">
      <Helmet>
        <title>Billing | Super User Manager</title>
        <meta
          name="description"
          content={t("dashboard.billings.description") as string}
        />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/control-panel/billing`}
        />
      </Helmet>

      <div className="flex w-full flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-gray-900">
            {t("dashboard.billings.title")}
          </h1>
          <p className="mt-0.5 text-sm text-gray-500">
            {t("dashboard.billings.description")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isFreePlan(user.customer_top_plan) &&
            trialStatus?.data &&
            !trialStatus.data.trial_info && (
              <CustomButton
                variant="secondary"
                onClick={() => setShowPromoCodeModal(true)}
                title={t("dashboard.promoCode.title")}
              />
            )}
          <CustomButton
            variant="primary"
            onClick={() => setShowPlansModal(true)}
            title={
              offersPlanChange
                ? pc("changePlan")
                : (t("dashboard.billings.btn-09") as string)
            }
            icon={offersPlanChange ? undefined : "plus"}
          />
        </div>
      </div>

      <div className="h-px w-full bg-gray-200"></div>

      <PlanChangeBanners
        customerId={customer_identification}
        entityId={user.entity_identification}
        planList={res?.planList}
        changes={planChanges}
      />

      <ActiveTrialBanner />

      {(canSendSms || (canSendWhatsapp && hasWhatsappSender)) && (
        <div className="w-full">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            {t("dashboard.billings.messagingCredits.title")}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {canSendSms && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <FaSms size={20} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">SMS</p>
                    <p className="text-xs text-gray-500">
                      {t("dashboard.billings.messagingCredits.balance", {
                        credits: smsCredit.toLocaleString(),
                      })}
                    </p>
                  </div>
                </div>
                <CustomButton
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSmsCreditModal(true)}
                  title={
                    t("dashboard.billings.messagingCredits.recharge") as string
                  }
                />
              </div>
            )}
            {canSendWhatsapp && hasWhatsappSender && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-600">
                    <FaWhatsapp size={20} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      WhatsApp
                    </p>
                    <p className="text-xs text-gray-500">
                      {t("dashboard.billings.messagingCredits.balance", {
                        credits: whatsappCredit.toLocaleString(),
                      })}
                    </p>
                  </div>
                </div>
                <CustomButton
                  variant="outline"
                  size="sm"
                  onClick={() => setShowWhatsappCreditModal(true)}
                  title={
                    t("dashboard.billings.messagingCredits.recharge") as string
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}

      <AddCreditModal
        show={showSmsCreditModal}
        onClose={() => setShowSmsCreditModal(false)}
        kind="sms"
      />
      <AddCreditModal
        show={showWhatsappCreditModal}
        onClose={() => setShowWhatsappCreditModal(false)}
        kind="whatsapp"
      />

      {res?.planList && res.planList.length > 0 && (
        <div className="w-full">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t("dashboard.billings.your-plans")}
          </h2>
          <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {res.planList.map((item: any, index: number) => (
              <PlanCard
                key={index}
                plan={item.plan}
                status={item.status}
                description={item.description}
                paidBy={item.paid_by ?? null}
                isSelected={selectedPlan === item.plan}
                onClick={() =>
                  handleOpenPlanDrawer(
                    item.plan,
                    item.status,
                    item.description,
                    item.paid_by ?? null
                  )
                }
              />
            ))}
          </div>
        </div>
      )}

      <SideDrawer
        open={showPlanDrawer}
        onClose={() => setShowPlanDrawer(false)}
        title={getPlanLabel(selectedPlan, selectedPlanLabel)}
        icon={HiCreditCard}
        footer={
          selectedPlan !== "My plans" &&
          (selectedPlanStatus !== "CANCELED" || selectedSelfPaid) &&
          !isFreePlan(selectedPlan) &&
          !isOneOffPurchasePlan(selectedPlan) &&
          selectedPlanPaidBy === null && (
            <div className="sticky bottom-0 left-0 right-0 -mx-4 -mb-4 border-t border-gray-200 bg-white px-4 py-3">
              <div className="flex flex-wrap items-center justify-end gap-2">
                <CustomButton
                  variant="secondary"
                  size="sm"
                  disabled={
                    !res?.planList || !selectedPlan || isOpeningCardUpdate
                  }
                  onClick={openCardUpdate}
                  title={t("dashboard.billings.btn-07") as string}
                />
                {selectedSelfPaid?.cancel_at_period_end ? (
                  <CustomButton
                    variant="primary"
                    size="sm"
                    disabled={isResuming}
                    isLoading={isResuming}
                    onClick={() =>
                      resumeSubscription(
                        {
                          customerId: customer_identification,
                          entityId: user.entity_identification,
                          plan: selectedSelfPaid.plan,
                        },
                        {
                          onSuccess: () => {
                            toast.success(pc("toasts.resumed"));
                          },
                        }
                      )
                    }
                    title={pc("banners.resume")}
                  />
                ) : (
                  <CustomButton
                    variant="danger"
                    size="sm"
                    disabled={!res?.planList || !selectedPlan}
                    onClick={() => setShowCancelModal(true)}
                    title={
                      selectedSelfPaid
                        ? pc("cancelSubscription.button")
                        : (t("dashboard.billings.btn-08") as string)
                    }
                  />
                )}
              </div>
            </div>
          )
        }
      >
        {selectedPlan !== "My plans" && (
          <div className="flex flex-col gap-4 pb-2">
            <div className="flex flex-wrap items-center gap-2">
              {selectedPlanStatus && renderPaymentStatus(selectedPlanStatus)}
              {getPlanTierDisplayName(selectedPlan) && (
                <StatusBadge color="gray" shape="full">
                  {getPlanTierDisplayName(selectedPlan)}
                </StatusBadge>
              )}
            </div>

            <p className="truncate text-xs text-gray-500" translate="no">
              <span className="font-medium text-gray-700">
                {t("dashboard.billings.plan-id")}:
              </span>{" "}
              {selectedPlan}
            </p>

            {selectedPlanPaidBy !== null &&
              (endsWithoutRenewal(selectedPlanPaidBy) ? (
                renderPartnerEndNotice(selectedPlanPaidBy)
              ) : (
                <div className="flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                  <p>
                    {selectedPlanPaidBy.display_name
                      ? t("dashboard.billings.providedByNotice", {
                          name: selectedPlanPaidBy.display_name,
                        })
                      : t("dashboard.billings.providedByPartnerNotice")}
                  </p>
                  {renderPartnerEmail(selectedPlanPaidBy.contact_email)}
                </div>
              ))}

            <div className={selectedPlanPaidBy !== null ? "hidden" : ""}>
              <h4 className="mb-2 text-sm font-semibold text-gray-900">
                {t("dashboard.billings.paymentMethods")}
              </h4>

              {fetchPlanCardsLoading ? (
                <div className="flex justify-center py-8" translate="no">
                  <Spinner size="sm" />
                </div>
              ) : planCards.length > 0 ? (
                <DataTable className="min-w-[560px]">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <DataTable.Head key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <DataTable.HeadCell
                          key={header.id}
                          className={
                            header.column.id === "Actions"
                              ? "text-right"
                              : undefined
                          }
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </DataTable.HeadCell>
                      ))}
                    </DataTable.Head>
                  ))}
                  <DataTable.Body>
                    {table?.getRowModel()?.rows?.map((row) => (
                      <DataTable.Row key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <DataTable.Cell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </DataTable.Cell>
                        ))}
                      </DataTable.Row>
                    ))}
                  </DataTable.Body>
                </DataTable>
              ) : (
                <EmptyState
                  icon={<HiCreditCard size={24} />}
                  title={t("dashboard.billings.toast-04")}
                />
              )}
            </div>

            <div>
              <h4 className="mb-2 text-sm font-semibold text-gray-900">
                {t("paymentHistory.title")}
              </h4>

              {fetchPlanHistoryLoading ? (
                <div className="flex justify-center py-8" translate="no">
                  <Spinner size="sm" />
                </div>
              ) : planHistoryData.length > 0 ? (
                <DataTable className="min-w-[480px]">
                  <DataTable.Head>
                    <DataTable.HeadCell>
                      {t("dashboard.billings.planHistoryModal.column-01")}
                    </DataTable.HeadCell>
                    {!isOneOffPurchasePlan(selectedPlan) && (
                      <DataTable.HeadCell>
                        {t("dashboard.billings.planHistoryModal.column-03")}
                      </DataTable.HeadCell>
                    )}
                    <DataTable.HeadCell>
                      {t("dashboard.billings.planHistoryModal.column-02")}
                    </DataTable.HeadCell>
                    <DataTable.HeadCell className="text-right">
                      {t("dashboard.billings.planHistoryModal.column-05")}
                    </DataTable.HeadCell>
                    {!isFreePlan(selectedPlan) &&
                      !isOneOffPurchasePlan(selectedPlan) && (
                        <DataTable.HeadCell className="text-right">
                          {t("dashboard.billings.planHistoryModal.column-04")}
                        </DataTable.HeadCell>
                      )}
                  </DataTable.Head>
                  <DataTable.Body>
                    {planHistoryData?.map((item: any, index: number) => (
                      <DataTable.Row key={index}>
                        <DataTable.Cell className="whitespace-nowrap font-medium tabular-nums text-gray-900">
                          {formatPayModDate(item.paymentDateTime, "lll")}
                        </DataTable.Cell>
                        {!isOneOffPurchasePlan(selectedPlan) && (
                          <DataTable.Cell className="whitespace-nowrap tabular-nums">
                            {moment(item.paymentNextDate).isValid()
                              ? moment(item.paymentNextDate).format("ll")
                              : "-"}
                          </DataTable.Cell>
                        )}
                        <DataTable.Cell className="whitespace-nowrap">
                          {renderPaymentStatus(item.paymentStatusEnum)}
                        </DataTable.Cell>
                        <DataTable.Cell className="whitespace-nowrap text-right tabular-nums font-medium text-gray-900">
                          {formatPaymentAmount(
                            item.paymentDetailMap?.price,
                            item.paymentDetailMap?.price_currency
                          )}
                        </DataTable.Cell>
                        {!isFreePlan(selectedPlan) &&
                          !isOneOffPurchasePlan(selectedPlan) && (
                            <DataTable.Cell className="whitespace-nowrap text-right">
                              {item.paymentDetailMap?.invoice_id &&
                              selectedPlanPaidBy === null ? (
                                <CustomButton
                                  variant="none"
                                  size="icon"
                                  className="ml-auto text-gray-400 enabled:hover:text-blue-600"
                                  disabled={isGettingInvoice}
                                  onClick={() =>
                                    handleGetInvoice(
                                      item.paymentDetailMap.invoice_id
                                    )
                                  }
                                  tooltip={
                                    t(
                                      "dashboard.billings.planHistoryModal.column-04"
                                    ) as string
                                  }
                                  icon="download"
                                />
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </DataTable.Cell>
                          )}
                      </DataTable.Row>
                    ))}
                  </DataTable.Body>
                </DataTable>
              ) : planHistoryError ? (
                <EmptyState
                  icon={<HiExclamationCircle size={24} />}
                  title={t("paymentHistory.error")}
                  description={t("paymentHistory.errorDescription")}
                />
              ) : (
                <EmptyState
                  icon={<HiCreditCard size={24} />}
                  title={t("paymentHistory.empty")}
                  description={t("paymentHistory.emptyDescription")}
                />
              )}
            </div>
          </div>
        )}
      </SideDrawer>

      <ConfirmModal
        show={showCancelModal}
        onClose={setShowCancelModal}
        title={
          selectedSelfPaid
            ? pc("cancelSubscription.title", {
                plan: pcPlanName(selectedSelfPaid.plan),
              })
            : (t("dashboard.billings.text-01") as string)
        }
        description={
          selectedSelfPaid
            ? pcDate(selectedPeriodEnd)
              ? pc("cancelSubscription.text", {
                  date: pcDate(selectedPeriodEnd),
                })
              : pc("cancelSubscription.textNoDate")
            : (t("dashboard.billings.text-02") as string)
        }
        cancelBtnName={
          selectedSelfPaid
            ? pc("cancelSubscription.keep")
            : (t("dashboard.billings.btn-02") as string)
        }
        cancelBtnFn={setShowCancelModal}
        confirmBtnName={
          selectedSelfPaid
            ? pc("cancelSubscription.confirm")
            : (t("dashboard.billings.btn-01") as string)
        }
        confirmBtnFn={handleCancelSelectedPlanSubscription}
        isLoading={isCanceling}
        ConfirmBtnColor="red"
      />

      <ConfirmModal
        show={showConfirmDeleteCard}
        onClose={setShowConfirmDeleteCard}
        title={t("dashboard.billings.DeleteCardConfirm.key1") as string}
        description={""}
        cancelBtnName={t("common.cancel") as string}
        cancelBtnFn={setShowConfirmDeleteCard}
        confirmBtnName={t("common.delete") as string}
        confirmBtnFn={handleRemoveCard}
        isLoading={isDeleting}
        ConfirmBtnColor="red"
      />

      <ModalShell
        show={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        size="7xl"
        footer={
          <CustomButton
            variant="primary"
            onClick={() => setShowPlansModal(false)}
            title={t("common.close") as string}
          />
        }
      >
        <Plans
          user_identification={customer_identification}
          entity_identification={user.entity_identification}
          isInBilling={true}
          targetPlan={upgradeTargetPlan}
          onCloseModal={() => setShowPlansModal(false)}
        />
      </ModalShell>

      <PromoCodeModal
        showModal={showPromoCodeModal}
        setShowModal={setShowPromoCodeModal}
        onSubmit={() => {
          refetch();
        }}
      />
    </div>
  );
};

export default Billing;
